import { TextInput, Button, PasswordInput, Space, createStyles, Text, Alert, Center } from '@mantine/core';
import { useForm } from '@mantine/form';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useState, useEffect } from 'react';
import { signInAuthUserWithEmailAndPassword } from '../../utils/firebase/firebase.utils';
import {  validateEmail, beigeColor } from '../../assets/constants';
import { useUserAuth } from '../../contexts/user.context';
import { useJuneProvider } from '../../contexts/analytics.context';
import { IconLogin } from '@tabler/icons-react';
import FormPaper from '../../components/form-paper/form-paper.component';
import LogoWithText from '../../components/logo-with-text/logo-with-text.component';

const useStyles = createStyles((theme) => ({
    outerContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        background: beigeColor,
        [theme.fn.smallerThan('xs')]: {
            alignItems: 'start',
            background: 'white'
        }
    },

    formContainer: {
        width: '100%',
        maxWidth: '460px',
    },

    submitButton: {
        width: '100%'
    },

    linkText: {
        textDecoration: 'none',
        fontWeight: 500,
        color: '#1959b0',
        '&:hover': {
            textDecoration: 'underline',
        }
    },

    mobileOnly: {
        [theme.fn.largerThan('sm')]: {
            display: 'none',
        }
    },


}));

const SignIn = () => {

    const { logOutMessage, setLogOutMessage } = useUserAuth();

    const analytics = useJuneProvider();

    const { classes } = useStyles();

    const navigate = useNavigate();

    const [formSubmitting, setFormSubmitting] = useState(false);
    const [signInProblem, setSignInProblem] = useState(false);

    // To prevent weird strict mode double render issues
    const [initialSignInMessage, setInitialSignInMessage] = useState(logOutMessage);
    
    useEffect(() => {
        setLogOutMessage('');
    }, [])

    const form = useForm({
        initialValues: {
            email: '',
            password: ''
        },

        validate: {
            email: (value) => validateEmail(value) ? null : 'Invalid email',
            password: (value) => value === '' ? 'Please enter your password' : null
        },

    });

    const signInHandler = async (event) => {
        setSignInProblem(false);
        event.preventDefault();
        form.validate();
        if (form.isValid()) {
            setFormSubmitting(true);
            try {
                const currentUser = await signInAuthUserWithEmailAndPassword(form.values.email, form.values.password);
                if (analytics) {
                    analytics.identify(currentUser.uid, {
                        email: form.values.email
                    });
                    analytics.track({
                        userId: currentUser.uid,
                        event: 'Signed in'
                    });
                }
                if (logOutMessage === 'Please sign in again to update your account.') {
                    navigate('/account');
                }
            } catch (error) {
                if (error.code === 'auth/user-not-found') {
                    form.setFieldError('email', "Couldn't find an account with that email");
                } else if (error.code === 'auth/wrong-password') {
                    form.setFieldError('password', 'Incorrect password');
                } else {
                    setSignInProblem(true);
                }
            }
        }

        setFormSubmitting(false);
    }

    return (
        <>
            <Helmet>
                <title>Sign in | Roundtable</title>
                <meta
                    name='description'
                    content='Sign in to your Roundtable account and get started with our data cleaning API.'
                />
            </Helmet>
            <div className={classes.outerContainer}>
                <div className={classes.formContainer}>
                    <FormPaper>
                        <Space h='lg' className={classes.mobileOnly} />
                        <Center>
                        <LogoWithText innerWidth = {200} innerHeight = {0} opacity={1} logoScalingFactor={1.1}/>
                        </Center>
                        <Space h='lg' />
                        <Text align="center" size='lg' weight={400}>Sign in to your account</Text>
                        <Text align="center" size='sm'>
                            Or <Text component={Link} to='/sign-up' className={classes.linkText} >sign up</Text>
                        </Text>
                        {
                            initialSignInMessage !== '' &&
                            <>
                                <Space h='md' />
                                <Alert color="teal" radius='lg'>{initialSignInMessage}</Alert>
                            </>
                        }
                        <Space h='md' />
                        <form name='sign-in' onSubmit={(event) => signInHandler(event)}>
                            <TextInput
                                withAsterisk={false}
                                name='email'
                                required
                                radius = 'md'
                                label="Email"
                                {...form.getInputProps('email')}
                                size='md'
                            />
                            <Space h='sm' />
                            <PasswordInput
                                withAsterisk={false}
                                name='password'
                                radius = 'md'
                                required
                                label="Password"
                                {...form.getInputProps('password')}
                                size='md'
                            />
                            <Text
                                size='sm'
                                className={classes.linkText}
                                component={Link} to='/forgot-password'
                            >
                                Forgot password?
                            </Text>
                            <Space h='lg' />
                            <Button
                                type='submit'
                                size='lg'
                                radius = 'md'
                                className={classes.submitButton}
                                leftIcon={formSubmitting === false && <IconLogin stroke={1.5}/>}
                                loading={formSubmitting}
                            >
                                {formSubmitting ? '' : 'Sign in'}
                            </Button>
                        </form>
                        <Space h='xs' />
                    </FormPaper>
                    {
                        signInProblem &&
                        <>
                            <Space h='md' />
                            <Alert title="Oops!" color="red" radius='lg'>There was a problem signing into your account. Please try again or <a href='mailto:support@roundtable.ai' target='_blank'>contact support</a>.</Alert>
                            <Space h='md' />
                        </>
                    }
                </div>
            </div>
        </>
    );
}

export default SignIn;